import { useState, useEffect, useCallback, useRef } from "react";
import samboardsLogo from "@/assets/samboards-logo.png";
import samboardOrangeMain from "@/assets/products/samboard-orange-main.png";
import librarySquaresCarousel from "@/assets/hero/library-squares-carousel-v2.png";
import sensoryPadsCarousel from "@/assets/hero/sensory-pads-carousel-v2.png";
import satpinCarousel from "@/assets/hero/satpin-carousel-v2.png";
import { ProductModal } from "@/components/ProductModal";
import { products, type Product } from "@/data/products";

type CarouselSlide = {
  id: string;
  type: "text" | "product";
  productName?: string;
  productSlug?: string;
  image?: string;
  title?: string;
  description?: string;
};

const slides: CarouselSlide[] = [
  {
    id: "text-slide",
    type: "text",
  },
  {
    id: "samboards",
    type: "product",
    productName: "Samboards",
    productSlug: "/samboards",
    image: samboardOrangeMain,
    title: "Samboards",
    description: "Full-size tactile boards for everyday regulation and grounding.",
  },
  {
    id: "library-squares",
    type: "product",
    productName: "Library Squares",
    productSlug: "/library-squares",
    image: librarySquaresCarousel,
    title: "Library Squares",
    description: "Quiet tactile tiles for libraries and shared spaces.",
  },
  {
    id: "sensory-pads",
    type: "product",
    productName: "Sensory Pads",
    productSlug: "/sensory-pads",
    image: sensoryPadsCarousel,
    title: "Sensory Pads",
    description: "Soft tactile pads for gentle, grounding sensory input.",
  },
  {
    id: "satpin",
    type: "product",
    productName: "Satpin Board",
    productSlug: "/satpin",
    image: satpinCarousel,
    title: "Satpin Board",
    description: "Educational sensory learning tool for early literacy.",
  },
];

export function HeroCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout>();
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Preload all carousel images
  useEffect(() => {
    const imagesToPreload = [samboardOrangeMain, librarySquaresCarousel, sensoryPadsCarousel, satpinCarousel];

    imagesToPreload.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // auto-advance
  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(nextSlide, 3000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPaused, nextSlide]);

  const handleSlideClick = () => {
    const slide = slides[currentSlide];
    if (slide.type === "product" && slide.productName) {
      const product = products.find((p) => p.name === slide.productName);
      if (product) {
        setSelectedProduct(product);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      prevSlide();
    } else if (e.key === "ArrowRight") {
      nextSlide();
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
    setIsPaused(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) nextSlide();
      else prevSlide();
    }

    setTimeout(() => setIsPaused(false), 3000);
  };

  const currentSlideData = slides[currentSlide];
  const isProductSlide = currentSlideData.type === "product";

  return (
    <>
      <div
        ref={carouselRef}
        className="relative flex items-center justify-center group cursor-grab active:cursor-grabbing touch-pan-y min-h-[400px] sm:min-h-[500px] md:min-h-[820px]"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onFocus={() => setIsPaused(true)}
        onBlur={() => setIsPaused(false)}
        onKeyDown={handleKeyDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        tabIndex={0}
        role="region"
        aria-label="Product carousel"
        aria-live="polite"
      >
        <div className="absolute -inset-10 bg-emerald-500/10 blur-3xl rounded-full pointer-events-none" />

        <div className="flex flex-col items-center gap-3">
          {/* Card */}
          <div
            className={`relative w-[220px] h-[220px] md:w-[280px] md:h-[280px] rounded-3xl bg-slate-900/70 border border-emerald-300/20 shadow-[0_22px_80px_rgba(15,118,110,0.7)] backdrop-blur-xl overflow-hidden ${
              isProductSlide ? "cursor-pointer" : ""
            }`}
            onClick={handleSlideClick}
          >
            {/* Slides */}
            <div className="relative h-full">
              {slides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    index === currentSlide ? "opacity-100" : "opacity-0 pointer-events-none"
                  }`}
                >
                  {slide.type === "text" ? (
                    // Text slide
                    <div className="p-6">
                      <p className="text-xs font-semibold tracking-wide text-emerald-200 mb-4 uppercase">
                        A focused sensory toolkit
                      </p>

                      <ul className="space-y-3 text-sm text-slate-100/85">
                        <li className="flex gap-3">
                          <span className="mt-1 h-1.5 w-3 rounded-full bg-emerald-400" />
                          <span>
                            <strong>Samboards</strong> for everyday regulation and tactile exploration.
                          </span>
                        </li>
                        <li className="flex gap-3">
                          <span className="mt-1 h-1.5 w-3 rounded-full bg-emerald-400" />
                          <span>
                            <strong>Library Squares</strong> for quiet, shared spaces.
                          </span>
                        </li>
                        <li className="flex gap-3">
                          <span className="mt-1 h-1.5 w-3 rounded-full bg-emerald-400" />
                          <span>
                            <strong>Sensory Pads</strong> for gentle, grounding touch.
                          </span>
                        </li>
                      </ul>

                      <div className="mt-6 border-t border-emerald-300/15 pt-4 flex items-center justify-between gap-3">
                        <div className="text-[11px] text-emerald-100/70">
                          <p className="font-semibold text-emerald-200">Used across NHS &amp; education</p>
                          <p>Trusted by therapists, care homes &amp; specialist schools.</p>
                        </div>
                        <img
                          src={samboardsLogo}
                          alt="Samboards Logo"
                          width={591}
                          height={344}
                          className="h-11 w-auto opacity-95"
                        />
                      </div>
                    </div>
                  ) : (
                    // Product image slide – full coverage
                    <div className="relative h-full w-full overflow-hidden">
                      <img
                        src={slide.image}
                        alt={slide.title}
                        className="h-full w-full object-contain object-center"
                        loading="eager"
                      />

                      {/* Gradient overlay */}
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/30 to-transparent" />

                      {/* Product info */}
                      <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                        <p className="text-xl font-semibold text-slate-50 mb-1.5">{slide.title}</p>
                        <p className="text-sm text-slate-200/90">{slide.description}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Pagination dots */}
          <div className="flex justify-center gap-0 carousel-pagination" role="tablist" aria-label="Carousel slides">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  goToSlide(index);
                }}
                className="rounded-full touch-manipulation p-2 -m-2"
                aria-label={`Go to slide ${index + 1}`}
                aria-selected={index === currentSlide}
                aria-current={index === currentSlide ? "true" : "false"}
                role="tab"
              />
            ))}
          </div>

          {/* Mobile swipe indicator */}
          <div className="md:hidden pointer-events-none">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-900/60 backdrop-blur-sm border border-emerald-400/20 text-[10px] text-slate-400">
              <svg className="w-3 h-3 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
              </svg>
              <span>Swipe</span>
            </div>
          </div>
        </div>
      </div>

      {selectedProduct && (
        <ProductModal
          isOpen={!!selectedProduct}
          onClose={() => setSelectedProduct(null)}
          product={selectedProduct}
          onAddToCart={() => {}}
        />
      )}
    </>
  );
}
